<template>
  <div id="volunteer-container">
    <div>
      <!-- // NOTE: ref="form" should match the value in the validate() method. -->
      <div v-if="submitted">
        <p class="align-center">Thanks for submitting your volunteer opportunity! One of our associates will review your submission and post it to the website. If not approved to post, we will contact you via email to address any issues we're seeing.</p>
      </div>
      <div v-else>
        <p>Submit your opportunity below, and we&rsquo;ll share it on our <a href="/about/volunteer">volunteer page</a> with our associates and guests to help you get volunteers</p>
        <v-form ref="form" v-model="valid" class="volunteer-opp-submission-form">
          <v-container>
            <v-layout row wrap>
              <v-flex md9>
                <v-layout row wrap>

                  <!-- // Heading - Organization Name -->
                  <v-flex xs12>
                    <h2>Organization Name</h2>
                  </v-flex>

                  <!-- // Field - Organization name -->
                  <v-flex xs12>
                    <v-text-field v-model="form.organizationName" :rules="rules.string" label="" required ></v-text-field>
                  </v-flex>

                  <!-- // Heading - Volunteer opportunity details -->
                  <v-flex xs12>
                    <h2>Volunteer opportunity details</h2>
                  </v-flex>

                  <!-- // Heading - Location -->
                  <v-flex xs12>
                    <h3>Location</h3>
                  </v-flex>

                  <v-layout v-for="(location,i) in form.locations" :key="i" class="location-address" row wrap>

                    <hr v-if="i!==0" />
                    <!-- // Field - Address -->
                    <v-flex xs12>
                      <h4>Event Site Name</h4>
                      <v-text-field v-model="form.locations[i].location" :rules="rules.string" required ></v-text-field>
                    </v-flex>

                    <!-- // Field - Address -->
                    <v-flex xs12>
                      <h4>Address</h4>
                      <v-text-field v-model="form.locations[i].address" :rules="rules.string" required ></v-text-field>
                    </v-flex>

                    <!-- // Field - City -->
                    <v-flex xs12 sm5>
                      <h4>City</h4>
                      <v-text-field v-model="form.locations[i].city" :rules="rules.string" required ></v-text-field>
                    </v-flex>

                    <!-- // Field - State -->
                    <v-flex xs12 sm3 offset-sm1>
                      <h4>State</h4>
                      <v-select v-model="form.locations[i].state" :rules="rules.string" :items="states" required single-line></v-select>
                    </v-flex>

                    <!-- // Field - Zip -->
                    <v-flex xs12 sm2 offset-sm1>
                      <h4>Zip Code</h4>
                      <v-text-field v-model="form.locations[i].zip" :rules="rules.string" required ></v-text-field>
                    </v-flex>

                  </v-layout>

                  <!-- // Manage location count -->
                  <template v-if="locationsGreaterThanOne">
                    <v-flex class="add-location align-center" @click="addLocation" xs12 sm6>
                      <p>Add another location</p>
                      <v-icon>add_circle_outline</v-icon>
                    </v-flex>
                    <v-flex class="remove-location align-center" @click="removeLocation" xs12 sm6>
                      <p>Remove location</p>
                      <v-icon>remove_circle_outline</v-icon>
                    </v-flex>
                  </template>
                  <template v-else>
                    <v-flex class="add-location align-center" @click="addLocation" xs12>
                      <p>Add another location</p>
                      <v-icon>add_circle_outline</v-icon>
                    </v-flex>
                  </template>

                  <!-- // Heading - Date -->
                  <v-flex xs8>
                    <h3>Date</h3>
                  </v-flex>

                  <v-flex xs4>
                    <p class="italics align-right"><label for="oneTimeOpp">Ongoing </label><input id="oneTimeOpp" v-model="form.oneTimeOpp" type="checkbox" /></p>
                  </v-flex>

                  <template v-if="form.oneTimeOpp">
                    <!-- // Field - Ongoing Field -->
                    <v-flex xs12>
                      <h4>When Will This Event Happen?</h4>
                      <v-text-field v-model="form.flexibleFrequency" :rules="rules.string" label="" required ></v-text-field>
                    </v-flex>
                  </template>
                  <template v-else>
                    <!-- // Field - Date From -->
                    <v-flex xs12>
                      <h4>From</h4>
                    </v-flex>
                    <v-flex sm3>
                        <v-select v-model="form.dateFromMonth" :rules="rules.string" :items="months" label="Month" required single-line>
                          <template slot="selection" slot-scope="{ item }">
                            {{ item.name }}
                          </template>
                          <template slot="item" slot-scope="{ item }">
                            {{ item.name }}
                          </template>
                        </v-select>
                    </v-flex>

                    <v-flex sm2>
                      <v-select v-model="form.dateFromDay" :rules="rules.string" :items="days" label="Day" required single-line></v-select>
                    </v-flex>

                    <v-flex sm2>
                      <v-select v-model="form.dateFromYear" :rules="rules.string" :items="years" label="Year" required single-line></v-select>
                    </v-flex>

                    <v-flex sm5 d-flex>
                      <v-select class="icon-adjust" v-model="form.dateFromHour" :rules="rules.string" :items="hours" label="HH" prepend-icon="alternate_email" required single-line></v-select>
                      <v-select v-model="form.dateFromMinute" :rules="rules.string" :items="minutes" label="MM" required single-line>
                        <template slot="selection" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                        <template slot="item" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                      </v-select>
                      <v-select v-model="form.dateFromAMPM" :rules="rules.string" :items="ampm" required single-line>
                        <template slot="selection" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                        <template slot="item" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                      </v-select>
                    </v-flex>

                    <!-- // Field - Date To -->
                    <v-flex xs12>
                      <h4>To</h4>
                    </v-flex>
                    <v-flex sm3>
                        <v-select v-model="form.dateToMonth" :rules="rules.string" :items="months" label="Month" required single-line>
                          <template slot="selection" slot-scope="{ item }">
                            {{ item.name }}
                          </template>
                          <template slot="item" slot-scope="{ item }">
                            {{ item.name }}
                          </template>
                        </v-select>
                    </v-flex>

                    <v-flex sm2>
                      <v-select v-model="form.dateToDay" :rules="rules.string" :items="days" label="Day" required single-line></v-select>
                    </v-flex>

                    <v-flex sm2>
                      <v-select v-model="form.dateToYear" :rules="rules.string" :items="years" label="Year" required single-line></v-select>
                    </v-flex>

                    <v-flex sm5 d-flex>
                      <v-select class="icon-adjust" v-model="form.dateToHour" :rules="rules.string" :items="hours" label="HH" prepend-icon="alternate_email" required single-line></v-select>
                      <v-select v-model="form.dateToMinute" :rules="rules.string" :items="minutes" label="MM" required single-line>
                        <template slot="selection" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                        <template slot="item" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                      </v-select>
                      <v-select v-model="form.dateToAMPM" :rules="rules.string" :items="ampm" required single-line>
                        <template slot="selection" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                        <template slot="item" slot-scope="{ item }">
                          {{ item.name }}
                        </template>
                      </v-select>
                    </v-flex>
                  </template>

                  <!-- // Field - Opportunity Title -->
                  <v-flex xs12 sm7>
                    <h3>Title</h3>
                    <v-text-field v-model="form.title" :rules="rules.string" label="" required ></v-text-field>
                  </v-flex>

                  <!-- // Field - Number of volunteers -->
                  <v-flex xs12 sm4 offset-sm1>
                    <h3># of volunteers needed</h3>
                    <v-select v-model="form.numberOfVolunteers" :items="volunteer_quantity_options" :rules="rules.notEmpty" required></v-select>
                  </v-flex>

                  <!-- // Field - Description -->
                  <v-flex xs12>
                    <h3>Description</h3>
                    <v-textarea v-model="form.description" :rules="rules.notEmpty" label="" value="" required></v-textarea>
                  </v-flex>

                  <!-- // Heading - Organization contact person -->
                  <v-flex xs12>
                    <h3>Select an area of interest</h3>
                  </v-flex>

                  <!-- // Field - Category -->
                  <v-flex xs12>
                    <v-select v-model="form.category" :items="filter_category_select" :rules="rules.notEmpty" required>
                      <template slot="selection" slot-scope="{ item }">
                        {{ item.name }}
                      </template>
                      <template slot="item" slot-scope="{ item }">
                        {{ item.name }}
                      </template>
                    </v-select>
                  </v-flex>

                  <!-- // Heading - Organization contact person -->
                  <v-flex xs12>
                    <h3>Organization contact person</h3>
                  </v-flex>

                  <!-- // Field - Full Name -->
                  <v-flex xs12>
                    <h4>Full Name</h4>
                    <v-text-field v-model="form.contactFullName" :rules="rules.string" required ></v-text-field>
                  </v-flex>

                  <!-- // Field - Title -->
                  <v-flex xs12>
                    <h4>Title</h4>
                    <v-text-field v-model="form.contactTitle" :rules="rules.string" required ></v-text-field>
                  </v-flex>

                  <!-- // Field - Phone -->
                  <v-flex xs12>
                    <h4>Phone Number</h4>
                    <v-text-field v-model="form.contactPhone" :rules="rules.string" required ></v-text-field>
                  </v-flex>

                  <!-- // Field - Email -->
                  <v-flex xs12>
                    <h4>Email Address</h4>
                    <v-text-field v-model="form.contactEmail" :rules="rules.email" required ></v-text-field>
                  </v-flex>

                  <!-- // Heading - Organization contact person -->
                  <v-flex xs12>
                    <h3>Registration Link</h3>
                  </v-flex>

                  <!-- // Field - Registration Link -->
                  <v-flex xs12>
                    <v-text-field v-model="form.registrationLink" :rules="rules.string" required ></v-text-field>
                  </v-flex>

                  <p class="hidden"><input type="text" v-model="form.non" /></p>

                  <!-- // Submit button. -->
                  <v-flex xs12 offset-md8 md4>
                    <v-btn :disabled="!valid" color="success" @click="validate">Submit Form</v-btn>
                  </v-flex>

                </v-layout>
              </v-flex>
              <!-- <v-flex hidden-sm-and-down sm12 md3> -->
              <v-flex sm12 md3>
                <h2>Upload Featured Image</h2>
                <div id="featured-image-placeholder">
                  <img :src="imageUrl" height="150" @click="pickFile" v-if="imageUrl" />
                  <div class="placeholder-item" @click="pickFile" v-else><v-icon x-large>insert_photo</v-icon></div>
                </div>
                <v-text-field label="Select Image" @click="pickFile" v-model="imageName" append-icon="attach_file"></v-text-field>
                <input type="file" style="display: none" ref="image" accept="image/*" @change="onFilePicked">
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios'
import { sendEmailFromTemplate } from '@/api/Mail'

export default {
  name: 'volunteer-opportunities-submission-form',
  props: {
    id: {
      type: String,
      required: false
    }
  },
  data: function () {
    return {
      submitted: false,
      volunteer_opportunity_categories: [],
      filter_category_select: [
      ],
      volunteer_quantity_options: [],
      dateToday: new Date(),
      response: '',
      days: null,
      months: [{ value: 1, name: 'January' }, { value: 2, name: 'February' }, { value: 3, name: 'March' }, { value: 4, name: 'April' }, { value: 5, name: 'May' }, { value: 6, name: 'June' }, { value: 7, name: 'July' }, { value: 8, name: 'August' }, { value: 9, name: 'September' }, { value: 10, name: 'October' }, { value: 11, name: 'November' }, { value: 12, name: 'December' }],
      years: null,
      hours: null,
      minutes: [{ value: '0', name: '00' }, { value: 5, name: '05' }, { value: 10, name: '10' }, { value: 15, name: '15' }, { value: 20, name: '20' }, { value: 25, name: '25' }, { value: 30, name: '30' }, { value: 35, name: '35' }, { value: 40, name: '40' }, { value: 45, name: '45' }, { value: 50, name: '50' }, { value: 55, name: '55' }],
      ampm: [{ value: 'am', name: 'AM' }, { value: 'pm', name: 'PM' }],
      form: {
        organizationName: '',
        locations: [
          { address: '', city: '', state: '', zip: '' }
        ],
        address: '',
        street: '',
        date: new Date().toISOString().substr(0, 10),
        menu: false,
        dateFromMonth: '',
        dateFromDay: '',
        dateFromYear: '',
        dateFromHour: '',
        dateFromMinute: '',
        dateFromAMPM: 'am',
        dateToMonth: '',
        dateToDay: '',
        dateToYear: '',
        dateToHour: '',
        dateToMinute: '',
        dateToAMPM: 'am',
        modalDateFrom: false,
        modalDateTo: false,
        title: '',
        numberOfVolunteers: '',
        contactFullName: '',
        contactTitle: '',
        contactEmail: '',
        contactPhone: '',
        registrationLink: '',
        description: '',
        category: '',
        oneTimeOpp: false,
        flexibleFrequency: '',
        non: '' // Honeypot
      },
      rules: {
        string: [
          v => !!v || 'This field is required'
        ],
        email: [
          v => !!v || 'E-mail is required',
          v => /.+@.+/.test(v) || 'E-mail must be valid'
        ],
        notEmpty: [
          v => !!v || 'This field is required'
        ]
      },
      valid: true,
      name: '',
      nameRules: [
        v => !!v || 'Name is required',
        v => (v && v.length <= 10) || 'Name must be less than 10 characters'
      ],
      select: null,
      items: [
        'Item 1',
        'Item 2',
        'Item 3',
        'Item 4'
      ],
      checkbox: false,
      menuFrom: false,
      menuTo: false,
      dialog: false,
      imageName: '',
      imageUrl: '',
      imageFile: '',
      imageWordPressID: '',
      states: [ 'Wisconsin', 'Alaska', 'Alabama', 'Arkansas', 'Arizona', 'California', 'Colorado', 'Connecticut', 'District of Columbia', 'Delaware', 'Florida', 'Georgia', 'Guam', 'Hawaii', 'Iowa', 'Idaho', 'Illinois', 'Indiana', 'Kansas', 'Kentucky', 'Louisiana', 'Massachusetts', 'Maryland', 'Maine', 'Michigan', 'Minnesota', 'Missouri', 'Mississippi', 'Montana', 'North Carolina', 'North Dakota', 'Nebraska', 'New Hampshire', 'New Jersey', 'New Mexico', 'Nevada', 'New York', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Puerto Rico', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Virginia', 'Virgin Islands', 'Vermont', 'Washington', 'West Virginia', 'Wyoming' ]
    }
  },
  computed: {
    locationsGreaterThanOne () {
      var locationCount = this.form.locations.length

      if (locationCount > 1) {
        return true
      } else {
        return false
      }
    }
  },
  methods: {
    createNumberRangeArray (min, max, extraZero = false) {
      var counter = min
      var returnArray = []

      for (counter; counter <= max; counter++) {
        if (extraZero) {
          returnArray.push(('0' + counter).slice(-2))
        } else {
          returnArray.push(counter)
        }
      }

      return returnArray
    },
    validate () {
      if (this.$refs.form.validate()) {
        this.snackbar = true
        if (this.imageUrl) {
          this.uploadImage()
        } else {
          this.createNewVolunteerOpportunity()
        }
      }
    },
    createNewVolunteerOpportunity () {
      var startDateTimestamp = Math.round(new Date(this.form.dateFromYear + '-' + this.form.dateFromMonth + '-' + this.form.dateFromDay + ' ' + this.form.dateFromHour + ':' + parseInt(this.form.dateFromMinute) + ':00 ' + this.form.dateFromAMPM + ' GMT').getTime()) / 1000
      var endDateTimestamp = Math.round(new Date(this.form.dateToYear + '-' + this.form.dateToMonth + '-' + this.form.dateToDay + ' ' + this.form.dateToHour + ':' + this.form.dateToMinute + ':00 ' + this.form.dateToAMPM + ' GMT').getTime()) / 1000

      var data = {
        'organization_name': this.form.organizationName,
        'title': this.form.title,
        'status': 'pending',
        'content': this.form.description,
        'street': this.form.address,
        'city': this.form.city,
        'state': this.form.state,
        'zip': this.form.zip,
        'dateFrom': this.form.dateFrom,
        'dateTo': this.form.dateTo,
        'contact_full_name': this.form.contactFullName,
        'contact_title': this.form.contactTitle,
        'contact_email': this.form.contactEmail,
        'contact_phone': this.form.contactPhone,
        'registration_link': this.form.registrationLink,
        'one_time_opp': !this.form.oneTimeOpp,
        'start_date': startDateTimestamp,
        'end_date': endDateTimestamp,
        'locations': this.form.locations,
        'locations_count': this.form.locations.length,
        'flexible_frequency': this.form.flexibleFrequency
      }

      if (this.form.numberOfVolunteers === 'See opportunity listing for details') {
        data['has_volunteer_limit'] = 0
      } else {
        data['has_volunteer_limit'] = 1
        data['volunteer_limit'] = this.form.numberOfVolunteers
      }

      if (this.form.category) {
        data['opportunity_category'] = this.form.category
      }

      if (this.imageWordPressID) {
        data['featured_media'] = this.imageWordPressID
      }

      axios({
        method: 'post',
        url: 'https://volunteer.festfoods.com/wp-json/wp/v2/volunteer_opp',
        // auth: {
        //   username: 'envano',
        //   password: 'zecWpUJLbZJZAy6bKAbjnNiYKjNhXVTW'
        // },
        headers: {
          'Authorization': 'Basic YXBpOnNAd1ZCdGlzI1Nhb3BZQHBOSEJzeShPeg=='
        },
        data: data
      })
        .then((response) => {
          this.response = response.data
          this.submitted = true
          this.sendNotificationEmail()
          this.scrollMeTo('app')
        })
        .catch(function (error) {
          this.response = error.data
        })
    },
    uploadImage () {
      axios({
        method: 'post',
        url: 'https://volunteer.festfoods.com/wp-json/wp/v2/media',
        // auth: {
        //   username: 'envano',
        //   password: 'zecWpUJLbZJZAy6bKAbjnNiYKjNhXVTW'
        // },
        headers: {
          'Authorization': 'Basic YXBpOnNAd1ZCdGlzI1Nhb3BZQHBOSEJzeShPeg==',
          'Content-Disposition': 'attachment; filename=' + this.imageName,
          'Content-Type': 'images/jpg'
        },
        data: this.imageFile
      })
        .then((response) => {
          this.response = response.data
          this.imageWordPressID = response.data.id
          this.createNewVolunteerOpportunity()
        })
        .catch(function (error) {
          this.response = error.data
        })
    },
    pickFile () {
      this.$refs.image.click()
    },
    onFilePicked (e) {
      const files = e.target.files
      if (files[0] !== undefined) {
        this.imageName = files[0].name
        if (this.imageName.lastIndexOf('.') <= 0) {
          return
        }
        const fr = new FileReader()
        fr.readAsDataURL(files[0])
        fr.addEventListener('load', () => {
          this.imageUrl = fr.result
          this.imageFile = files[0] // this is an image file that can be sent to server...
        })
      } else {
        this.imageName = ''
        this.imageFile = ''
        this.imageUrl = ''
      }
    },
    getVolunteerOpportunityCategories () {
      axios({
        method: 'get',
        url: 'https://volunteer.festfoods.com/wp-json/wp/v2/opportunity_category?per_page=100'
      })
        .then((response) => {
          // Store our category taxonomy options.
          this.volunteer_opportunity_categories = response.data

          for (var i = 0; i < this.volunteer_opportunity_categories.length; i++) {
            this.filter_category_select.push({
              value: this.volunteer_opportunity_categories[i].id,
              name: this.volunteer_opportunity_categories[i].name
            })
          }
        }).catch(function (error) {
          this.error = error.data
        })
    },
    addLocation () {
      this.form.locations.push({ address: '', city: '', state: '', zip: '' })
    },
    removeLocation () {
      this.form.locations.pop()
    },
    scrollMeTo (id) {
      event.preventDefault()
      const scrollToElement = document.querySelector(`#${id}`)
      const scrollElementOffset = this.getScrollOffset(scrollToElement)
      window.scroll(0, (scrollElementOffset))
    },
    sendNotificationEmail () {
      // Need to send mailgun email to a Festival email address yet to be decided.
      const from = 'noreply@festfoods.com'
      const to = 'SPetersen@festfoods.com'
      const subject = 'New Volunteer Opportunity submission'
      const template = 'volunteer-opportunity-notification'
      const data = {}
      sendEmailFromTemplate(from, to, subject, template, data)
    }
  },
  created: function () {
    this.getVolunteerOpportunityCategories()
    this.volunteer_quantity_options = this.createNumberRangeArray(1, 100)
    this.volunteer_quantity_options.unshift('See opportunity listing for details')
    this.days = this.createNumberRangeArray(1, 31, true)
    this.years = this.createNumberRangeArray(this.dateToday.getFullYear(), this.dateToday.getFullYear() + 10)
    this.hours = this.createNumberRangeArray(1, 12, true)
  }
}
</script>

<style lang="scss">
#vol-opp-list {
    margin: 0 auto;
    width: 1000px;
}

@media (max-width: 1100px) {
    #vol-opp-list {
        width: 90%;
    }
}

.italics {
  color: #999999;
  font-style: italic;
}

/**
* Buttons
**/
.button-action,
a.button-action {
    color: #fff;
    font-size: 1rem;
    text-decoration: none;
}

    .button-action-green,
    a.button-action-green {
        background-color: #3a823d;
    }

        a:hover.button-action-green {
            background-color: #416e3c;
        }

/**
* Spacing
**/
.margin-bottom {
    margin-bottom: 50px;
}
#volunteer-container {
  .application {
    background: transparent;
  }

  /**
  * Filter Form
  **/
  .volunteer-opp-filter-form {
    .v-input {
      display: inline-block;
      margin-right: 2%;
      margin-top: 0;
      width: 36%;
    }

    .v-input__slot {
      background-color: #f5f5f5;
      padding: 9px;
    }

    .v-input__slot:before {
      border:none;
    }

    .v-input__append-inner {
      background-color: #3a823d;
      padding: 0;
    }

    .theme--light.v-icon {
      color: #FFFFFF;
    }

    .button-action {
      font-weight: bold;
    }

    .v-input:first-child {
      margin-left: 0;
    }

    .v-btn {
      margin: 0px;
      padding: 0;
      width: 24%;
    }

    @media(max-width:800px) {
      .v-input {
        margin: 0;
        padding: 0;
        width: 100%;
      }

      .v-btn {
        margin: 0;
        width: 100%;
      }
    }
  }

  .theme--light.v-btn:not(.v-btn--icon):not(.v-btn--flat)
  {
    background-color: #3a823d;
    border-radius: 0;
    box-shadow: none;
    color: #FFFFFF;
    height: 48px;
  }

  .theme--light.v-btn:not(.v-btn--icon):not(.v-btn--flat):hover
  {
    background-color: #416e3c;
  }

  /**
  * List
  **/
  .v-expansion-panel {
    box-shadow: none;

    .v-expansion-panel__header {
      padding: 20px 24px 20px 0;
    }

    .v-expansion-panel__body {
      padding-bottom: 40px;
    }
  }

  .volunteer-opportunities-list {
    border-bottom: 1px solid rgba(0,0,0,.12);
    border-top: 1px solid rgba(0,0,0,.12);

    .v-list--two-line .v-list__tile {
      height: 50px;
    }
  }

    // Volunteer opportunity
    .volunteer-opp {
      .v-expansion-panel__header {
        font-size: 1.3rem;
        font-weight: bold;
      }

      .v-expansion-panel__header span {
        font-size: 1rem;
        font-weight: normal;
        margin-left: 0;
      }

      .v-expansion-panel__header span:last-child {
        margin-left: 10px;
      }

      h2 {
        font-size: 1.4rem;
        margin-bottom: 4px;
        margin-top: 30px;
        text-transform: uppercase;

        &:first-child {
          margin-top: 0;
        }
      }
    }

    // Grey block
    .grey-block-graphic {
      overflow: hidden;

      .v-chip {
        position: absolute;
        right: 7px;
        top: 8px;
      }

      img {
        display: block;
        max-width: 100%;
      }
    }

    .grey-block {
      background-color: #e9e7e8;
      margin-bottom: 25px;
      padding: 30px 40px;

      p {
        margin-bottom: 0;
      }

      .button-action {
        display: inline-block;
        font-size: 1.15rem;
        margin-top: 30px;
        padding: 14px 0;
        text-align: center;
        text-transform: uppercase;
        width: 100%;
      }
    }

  /**
  * Submission form
  **/
  .hidden {
    display: none;
  }

  .volunteer-opp-submission-form {
    // Headings
    h2 {
      font-size: 1.4rem;
      margin-bottom: 20px;
      margin-top: 30px;
      text-transform: uppercase;
    }

    h3 {
      color: #919191;
      font-size:1.3rem;
      margin-bottom: 20px;
      text-transform: uppercase;
    }

    h4 {
      color: #919191;
      font-size:1.1rem;
    }

    .location-address hr {
      border-top: 1px solid rgba(0, 0, 0, 0.15);
      margin-bottom: 20px;
      margin-top: 10px;
      width: 100%;
    }

    // Inputs
    .v-input {
      padding-top: 0;
    }

    .v-input__slot {
      background-color: #f5f5f5;
      border-bottom: 2px solid #666666;
      padding: 10px;
    }

    .theme--light.v-text-field>.v-input__control>.v-input__slot:before {
      border-color: #f5f5f5;
    }

    .add-location,
    .remove-location {
      color: #8b8b8b;
      cursor: pointer;
      margin-bottom: 30px;

      .theme--light.v-icon {
        color: #c2c2c2;
      }
    }

    .icon-adjust {
      .v-input__prepend-outer {
        left: 4px;
        position: relative;
        top: 10px;
      }

      .v-select__selections,
      .v-select__selections label.v-label.theme--light {
        font-family: Roboto, sans-serif;
      }
    }

    .placeholder-item {
      background-color: #f5f5f5;
      text-align: center;
      padding: 33%;
      width: 100%;
    }

    .v-btn {
      padding-left: 0;
      padding-right: 0;
      margin-left: 0;
      margin-right: 0;
      width:100%;
    }

    #featured-image-placeholder {
      border: 1.75px dashed #CCCCCC;
      margin-bottom: 20px;
      padding: 5px;

      img {
        display: block;
        height: auto;
        width:100%;
      }
    }
  }
}

.v-form > .container > .layout > .flex.md3 {
  align-content: flex-start;
  flex-flow: row wrap;

  #featured-image-placeholder {
    width: 100%;
  }
}

.v-form > .container > .layout .flex {
  display: unset;
}
</style>
